import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { useLink } from '@grandvisionhq/frk-routing';
import { getProductKeyFromCTType } from '@grandvisionhq/utils';
import '../types/product.js';
import { isSubscriptionPurchase, isInStoreSubscriptionPurchase } from '../types/typeguards.js';
import { getDisplayPrice } from '../utils/get-display-price.js';
import '@grandvisionhq/svc-tealium/utils';
import { updatePurchaseType } from './actions.js';
import { productDetailContextReducer, initialState } from './reducer.js';

/**
 * Product Context
 */ const ProductContext = /*#__PURE__*/ React.createContext({});
ProductContext.displayName = 'ProductContext';
/**
 * Product Provider
 */ function ProductProvider({ children , sku , selfUrl , product , routes , facetConfigList , specificationConfiguration , bookAppointmentRouteConfig  }) {
    const { variants  } = product;
    const [state, dispatch] = React.useReducer(productDetailContextReducer, swcHelpers.objectSpread({}, initialState, {
        activeSku: sku
    }));
    /**
   * Typescript v4 is unable to handle union types with the .find() method.
   * More information here: https://github.com/microsoft/TypeScript/issues/44373
   *
   * The issue is fixed in v5, so we can remove the @ts-expect-error and the
   * cast for `const activeVariant: Variant` when we upgrade.
   */ const activeVariant = React.useMemo(// @ts-expect-error ts(2349)
    ()=>variants.find((variant)=>variant.sku === state.activeSku
        ) || variants[0]
    , [
        state,
        variants
    ]);
    const { selectedPurchaseType  } = state;
    // Reset the selected purchase type if the active variant does not support the current purchase type selection
    React.useEffect(()=>{
        const { isAvailableForInStoreSubscription , isAvailableForSubscription  } = activeVariant;
        if (isSubscriptionPurchase(selectedPurchaseType) && !isAvailableForSubscription || isInStoreSubscriptionPurchase(selectedPurchaseType) && !isAvailableForInStoreSubscription) {
            dispatch(updatePurchaseType({
                purchaseType: 'single-purchase'
            }));
        }
    }, [
        activeVariant,
        selectedPurchaseType
    ]);
    // Update the display price based on the selected purchase type (single purchase or subscription)
    const displayPrice = React.useMemo(()=>getDisplayPrice(activeVariant, state)
    , [
        activeVariant,
        state
    ]);
    const productLink = useLink({
        route: 'products.detail',
        params: {
            slug: activeVariant.slug,
            sku: activeVariant.sku,
            key: getProductKeyFromCTType(product.productType)
        }
    });
    const productUrl = `${selfUrl}${productLink === null || productLink === void 0 ? void 0 : productLink.href}`;
    const value = React.useMemo(()=>({
            activeVariant,
            bookAppointmentRouteConfig,
            dispatch,
            displayPrice,
            facetConfigList,
            product,
            productUrl,
            routes,
            selectedPurchaseType,
            specificationConfiguration
        })
    , [
        activeVariant,
        bookAppointmentRouteConfig,
        displayPrice,
        facetConfigList,
        product,
        productUrl,
        routes,
        selectedPurchaseType,
        specificationConfiguration, 
    ]);
    return /*#__PURE__*/ React.createElement(ProductContext.Provider, {
        value: value
    }, children);
}

export { ProductContext, ProductProvider };
//# sourceMappingURL=ProductContext.js.map
